import CreateAxiosInstance from './axiosConfig';
import { readReferralAttribution } from '../utils/referralAttribution';
const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

export async function addUser(packetId, token, customErrorHandler) {
  // The only call that carries attribution — it's what creates a Diffit account, and it
  // re-attributes on every sign-in, which is how a teacher who followed a referral link
  // while already signed in gets picked up. readReferralAttribution() returns null when
  // there's nothing to attribute, which spreads to nothing; the caller clears the capture.
  //
  // customErrorHandler is for the referral drain, which runs on an ordinary page load for
  // a teacher who is already signed in and working: the default message ("There was an
  // error logging you in") would be both wrong and a blocking modal. Registration passes
  // nothing and keeps the default.
  return await APIClient.post(
    '/user',
    { packet_id: packetId, ...readReferralAttribution() },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      ...(customErrorHandler
        ? { customErrorHandler }
        : { defaultErrorMessage: 'There was an error logging you in. Please try again later.' }),
    }
  );
}

export async function markUserNuxAsSeen(userId, seenNux, customErrorHandler) {
  return await APIClient.post(
    `/user/${userId}/nux`,
    { seen_nux: seenNux },
    {
      // Pass a handler to suppress the global error modal.
      customErrorHandler: customErrorHandler || null,
      defaultErrorMessage: 'There was an error logging you in. Please try again later.',
    }
  );
}

export async function getCurrentUser(customErrorHandler) {
  return await APIClient.get('/user', {
    customErrorHandler: customErrorHandler || null,
  });
}

export async function isGoogleOAuthTokenValid() {
  return await APIClient.get('/user/valid_google_oauth_token', {
    defaultErrorMessage: 'There was an error logging you in. Please try again later.',
  });
}

export async function reportContent(resourceType, content) {
  return await APIClient.post(
    `/user/report`,
    { resource_type: resourceType, content: content },
    {
      defaultErrorMessage: 'Error reporting content',
    }
  );
}

export async function submitSurveyTrialExtension(survey) {
  return await APIClient.post(
    '/user/survey',
    {
      survey_response: survey,
    },
    {
      defaultErrorMessage: 'Error submitting survey',
    }
  );
}

export async function updateProfileImage(file) {
  const formData = new FormData();
  formData.append('profile_image', file);
  return await APIClient.post('/user/profile_image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    defaultErrorMessage: 'There was an error updating your profile picture. Please try again.',
  });
}

// Email the user's school admins about their Diffit work. The backend requires the
// recipients to share the sender's (non-consumer) email domain and rejects the request
// otherwise, so the modal renders the error inline rather than in the global dialog.
export async function shareWithAdmin({ emails, note, packetId }) {
  return await APIClient.post(
    '/user/share-with-admin',
    { emails, note: note || null, packet_id: packetId || null },
    {
      customErrorHandler: () => {},
    }
  );
}

export async function requestCurriculum({ emails, note, curriculumId, curriculumName }) {
  return await APIClient.post(
    '/user/request-curriculum',
    {
      emails,
      note: note || null,
      curriculum_id: curriculumId,
      curriculum_name: curriculumName,
    },
    { customErrorHandler: () => {} }
  );
}

// Which "get 30 more days" ask this teacher's paygates should make, if any.
// Read once per session (see PremiumOfferProvider) — the count behind it is a DB query.
export async function getPremiumOffer() {
  return await APIClient.get('/user/premium-offer', {
    // Paygates fall back to the plans link when this fails; a dialog over a gate the
    // teacher didn't ask about would be worse than the fallback.
    customErrorHandler: () => {},
  });
}
